import SearchIconBtn from '../../assets/icons/SearchIconBtn'

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { AssignEntityForm } from '.'
import {
  assignUserToEntity,
  deleteAssignedUserEntity,
  deleteAssignedUserUpdateEntity,
  downloadAssignedUserExcel,
  getAssignedUserEntityList,
} from '../../api/AssignEntityApis'
import { getEntityList } from '../../api/GetEntityMasterApi'
import { getTANList } from '../../api/adminTAN'
import { getUsers } from '../../api/adminUser'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import { columns, searchFields } from '../../constants/assignEntityColumn.js'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { DEFAULT_PAGE, getValidCriteria, sanitizeForLog } from '../../constants/index.js'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { CommonModal, CommonTable, ConfirmationDialog, Search } from '../common'
import {saveBlobAsFile} from "../../api/apiUtils.js"

const AssignEntity = ({ roleConfig, roleTypes }) => {
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState(null)
  const [formLoading, setFormLoading] = useState(false)

  // Edit User modal state
  const [editUserOpen, setEditUserOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Entities and users state
  const [entities, setEntities] = useState([])
  const [activeUsers, setActiveUsers] = useState([])
  const [tan, setTan] = useState([])

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const { showSnackbar } = useSnackbar()

  // Assign Entity Form state (multiple rows)
  const [assignEntityRows, setAssignEntityRows] = useState([
    {
      id: 1,
      userId: '',
      username: '',
      email: '',
      businessRole: [],
      entityNames: [],
      entityTANs: [],
      ssoUser: false,
    },
  ])

  const [formErrors, setFormErrors] = useState({})

  // Ref to prevent double API calls in StrictMode
  const hasLoadedData = useRef(false)

  const loadAllData = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList = []
  ) => {
    setLoading(true)
    try {
      // Load all data concurrently
      const [
        assignmentsResponse,
        usersResponse,
        entitiesResponse,
        tanResponse,
      ] = await Promise.all([
        getAssignedUserEntityList({
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize: pageSize,
            sortField: 'lastModifiedDate',
            sortOrder: 'DESC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: searchList,
        }),
        getUsers({
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: DEFAULT_PAGE,
            sortField: 'lastModifiedDate',
            sortOrder: 'DESC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: roleTypes === 'ADMIN'
            ? [
              {
                fieldName: 'role',
                operator: 'EQUAL_TO',
                value: 'PREPARER',
              },
            ]
            : [],
        }),
        getEntityList({
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: DEFAULT_PAGE,
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
        }),
        getTANList({
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: DEFAULT_PAGE,
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
        }),
      ])

      // Process assignments
      if (assignmentsResponse.success) {
        const assignmentsData = assignmentsResponse.data?.data?.content || []

        setTotalItems(assignmentsResponse.data?.data?.totalElements)
        setCurrentPage(page)
        const transformedAssignments = assignmentsData.map((item, index) => ({
          id: item.id || index + 1,
          userId: item.userId || '',
          username: item.username || '',
          name: item.name || '',
          email: item.email || '',
          businessRole: item.businessRole || '',
          entityName: item.entityName || '',
          taxDeductionAccountNumber: item.taxDeductionAccountNumber || '',
          assignedBy: item.createdBy || 'System',
          assignedDate:
            item.createdDate || new Date().toISOString().split('T')[0],
          updatedBy: item.lastModifiedBy || 'System',
          lastModifiedDate:
            item.lastModifiedDate || new Date().toISOString().split('T')[0],
          uuid: item.uuid || '',
        }))

        setAssignments(transformedAssignments)
      } else {
        showSnackbar(assignmentsResponse.error)
      }

      // Process users
      if (usersResponse && usersResponse.data && usersResponse.data.content) {
        const usersData = usersResponse.data.content.map(user => ({
          id: user.userId,
          userId: user.username, // Use username as the display userId
          username: user.name,
          email: user.emailId,
          businessRole: user.role,
          active: user.isActive,
          numericUserId: user.userId, // Keep the numeric ID for API calls
        }))
        // Filter only active users
        const activeUsersData = usersData.filter(user => user.active)
        setActiveUsers(activeUsersData)
      } else {
        console.error('Failed to load users: Invalid response format')
      }
      // Process entities
      if (
        entitiesResponse.success &&
        entitiesResponse.data &&
        entitiesResponse.data.data &&
        entitiesResponse.data.data.content
      ) {
        const entitiesData = entitiesResponse.data.data.content.map(entity => ({
          id: entity.uuid || entity.id,
          uuid: entity.uuid,
          name: entity.entityName,
          // Store the full entity object for debugging
          fullEntity: entity,
        }))
        setEntities(entitiesData)
      } else {
        showSnackbar(entitiesResponse.error)
      }
      // Process TANs with entity associations
      if (tanResponse.content) {
        const tanData = tanResponse.content.map(tanEntity => ({
          tan: tanEntity.taxDeductionAccountNumber,
          entityName: tanEntity.entityName,
          entityUuid: tanEntity.uuid,
          // Include any other relevant fields from the API response
          ...tanEntity,
        }))
        setTan(tanData)
      } else {
        showSnackbar(tanResponse?.error)
      }
    } catch (error) {
      console.error('Error loading data:')
    } finally {
      setLoading(false)
    }
  }

  // Load all data on component mount
  useEffect(() => {
    // Prevent double calls in React StrictMode
    if (hasLoadedData.current) return
    hasLoadedData.current = true

    loadAllData()
  }, [])

  const validateAssignEntityForm = () => {
    const errors = {}

    assignEntityRows.forEach((row, index) => {
      if (!row.userId) {
        errors[`userId_${index}`] = 'User ID is required'
      }
      if (!row.entityNames || row.entityNames.length === 0) {
        errors[`entityNames_${index}`] = 'At least one Entity Name is required'
      }
      if (!row.entityTANs || row.entityTANs.length === 0) {
        errors[`entityTANs_${index}`] = 'At least one Entity TAN is required'
      }

      // Check if user is trying to update to the same user (only during edit)
      if (editingAssignment && row.userId) {
        const originalUserId = editingAssignment.userId
        const newUserId = row.userId

        if (originalUserId === newUserId) {
          errors[`sameUser_${index}`] =
            'The selected user is the same as the current user. Please select a different user.'
        }
      }

      // Check for duplicate assignments
      if (row.userId && row.entityNames && row.entityTANs) {
        row.entityNames.forEach(entityName => {
          row.entityTANs.forEach(taxDeductionAccountNumber => {
            const existingAssignments = assignments.filter(
              assignment =>
                assignment.userId === row.userId &&
                assignment.entityName === entityName &&
                assignment.taxDeductionAccountNumber ===
                taxDeductionAccountNumber &&
                (!editingAssignment || assignment.id !== editingAssignment.id)
            )

            if (existingAssignments.length > 0) {
              errors[`duplicate_${index}`] =
                'Selected Entity(s) and TAN(s) are already assigned to this user'
            }
          })
        })
      }
    })

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleDownload = async () => {
    // Check if there's no data to download
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    try {
      const payload = {
        pageFilter: {
          currentPage: currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: getValidCriteria(searchCriteria),
      }
      const blob = await downloadAssignedUserExcel(payload)
      if (!blob) {
        showSnackbar('Failed to generate file', 'error');
        return;
      }

      // ✅ Veracode-safe download handler
      saveBlobAsFile(blob, 'assigned_user_entities.xlsx');
    } catch (e) {
      console.error('Failed to download excel', sanitizeForLog(e))
    }
  }

  const handleAssignEntity = () => {
    setEditingAssignment(null)
    setAssignEntityRows([
      {
        id: 1,
        userId: '',
        username: '',
        email: '',
        businessRole: [],
        entityNames: [],
        entityTANs: [],
      },
    ])
    setFormErrors({})
    setModalOpen(true)
  }

  const handleEdit = assignment => {
    const matchedUser = activeUsers.find(
      u =>
        u.numericUserId === assignment.userId || u.userId === assignment.userId
    )

    setAssignEntityRows([
      {
        userId: matchedUser ? matchedUser.userId : assignment.username,
        username:
          assignment.username ||
          (matchedUser ? matchedUser.username : '') ||
          '',
        // Do not prefill email and businessRole per request
        email: matchedUser.email,
        businessRole: [matchedUser.businessRole],
        // Keep entity selections from the row if present (not shown in edit modal)
        entityNames: assignment.entityName ? [assignment.entityName] : [],
        entityTANs: assignment.taxDeductionAccountNumber
          ? [assignment.taxDeductionAccountNumber]
          : [],
        ssoUser: !!assignment.ssoUser,
      },
    ])
    setFormErrors({})
    setEditingAssignment(assignment)
    setEditUserOpen(true)
  }

  const handleDelete = assignment => {
    setDeleteTarget(assignment)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      const userId = deleteTarget.uuid
      await deleteAssignedUserEntity(userId)
      loadAllData(currentPage, itemsPerPage, getValidCriteria(searchCriteria))
      setDeleteDialogOpen(false)
      setDeleteTarget(null)
    } catch (e) {
      showSnackbar('Error deleting assignment', 'error')
    }
  }

  const handleAssignEntityRowChange = (rowIndex, field, value) => {
    setAssignEntityRows(prev =>
      prev.map((row, index) => {
        if (index === rowIndex) {
          const updatedRow = { ...row, [field]: value }

          // Ensure entityNames and entityTANs are always arrays
          if (field === 'entityNames' && typeof value === 'string') {
            updatedRow.entityNames = value.split(',').map(item => item.trim()) // Convert string to array
          }

          if (field === 'entityTANs' && typeof value === 'string') {
            updatedRow.entityTANs = value.split(',').map(item => item.trim()) // Convert string to array
          }

          // Auto-populate user details when userId changes
          if (field === 'userId') {
            const selectedUser = activeUsers.find(user => user.userId === value)
            if (selectedUser) {
              updatedRow.username = selectedUser.username
              updatedRow.email = selectedUser.email
              updatedRow.businessRole = selectedUser.businessRole
                .split(', ')
                .map(item => item.trim())
            } else {
              updatedRow.username = ''
              updatedRow.email = ''
              updatedRow.businessRole = []
            }
          }

          // Filter TANs based on selected entities
          if (field === 'entityNames') {
            // Get available TANs for the selected entities from the TAN list
            const availableTANs = tan
              .filter(tanItem => value.includes(tanItem.entityName))
              .map(tanItem => tanItem.tan)
            // Remove duplicates
            const uniqueAvailableTANs = [...new Set(availableTANs)]
            // Filter existing selected TANs to only include those available for selected entities
            updatedRow.entityTANs = updatedRow.entityTANs.filter(selectedTan =>
              uniqueAvailableTANs.includes(selectedTan)
            )
          }
          return updatedRow
        }
        return row
      })
    )

    // Clear error when user starts typing
    const errorKey = `${field}_${rowIndex}`
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: '',
      }))
    }
  }

  const handleAddAssignEntityRow = () => {
    const newId = Math.max(...assignEntityRows.map(row => row.id)) + 1
    setAssignEntityRows(prev => [
      ...prev,
      {
        id: newId,
        userId: '',
        username: '',
        email: '',
        businessRole: [],
        entityNames: [],
        entityTANs: [],
      },
    ])
  }

  const handleRemoveAssignEntityRow = rowIndex => {
    if (assignEntityRows.length > 1) {
      setAssignEntityRows(prev => prev.filter((_, index) => index !== rowIndex))
    }
  }

  const handleFormSubmit = async e => {
    e.preventDefault()
    const isValid = validateAssignEntityForm()
    if (!isValid) {
      return
    }

    setFormLoading(true)

    try {
      const currentDate = new Date().toISOString().split('T')[0]

      if (editingAssignment) {
        // Build entityTanMasterDTOs payload from edited row
        const row = assignEntityRows[0] || {}

        // Resolve numeric user id from selected username (row.userId)
        const selectedUser = activeUsers.find(u => u.userId === row.userId)
        const numericUserId = selectedUser ? selectedUser.numericUserId : 0

        const entityTanMasterDTOs = []
          ; (row.entityNames || []).forEach(entityName => {
            const entity = entities.find(e => e.name === entityName)
            if (!entity) return
            const selectedTansForEntity = (row.entityTANs || []).filter(t =>
              tan.some(ti => ti.entityName === entityName && ti.tan === t)
            )
            if (selectedTansForEntity.length > 0) {
              const tanIds = selectedTansForEntity.map(tanVal => {
                const match = tan.find(
                  ti => ti.tan === tanVal && ti.entityName === entityName
                )
                return match?.uuid || match?.id || tanVal
              })
              entityTanMasterDTOs.push({
                userId: numericUserId,
                entityMasterUuid: entity.uuid || entity.id,
                tanIds,
                ssoUser: !!row.ssoUser,
              })
            }
          })

        const response = await assignUserToEntity(entityTanMasterDTOs)
        if (!response?.success) {
          showSnackbar(
            'Failed to update assignment: ' +
            (response?.error || 'Unknown error'),
            'error'
          )
          setFormLoading(false)
          return
        }

        // Close edit modal on success
        setEditUserOpen(false)

        // Delete previous assignments for old userId after successful update
        try {
          const prevUserId = editingAssignment.userId
          if (prevUserId) {
            await deleteAssignedUserUpdateEntity(prevUserId)
          }
        } catch (e) {
          console.error('Failed to delete previous assignments')
        }

        await loadAllData(1, itemsPerPage, [])
      } else {
        // Prepare data for API call
        const entityTanMasterDTOs = []

        assignEntityRows.forEach(row => {
          // Find the user ID using the username (userId in our transformed data)
          const selectedUser = activeUsers.find(
            user => user.userId === row.userId
          )
          const numericUserId = selectedUser ? selectedUser.numericUserId : 0

          // Create combinations of entities and TANs
          row.entityNames.forEach(entityName => {
            const entity = entities.find(e => e.name === entityName)
            if (entity) {
              // Get TANs for this entity from the TAN list
              const entityTANs = tan.filter(
                tanItem => tanItem.entityName === entityName
              )

              // Filter to only include selected TANs
              const selectedTANs = row.entityTANs.filter(selectedTan =>
                entityTANs.some(tanItem => tanItem.tan === selectedTan)
              )

              if (selectedTANs.length > 0) {
                // Get TAN IDs/UUIDs for the selected TANs
                const tanIds = selectedTANs.map(selectedTan => {
                  const tanItem = tan.find(
                    t => t.tan === selectedTan && t.entityName === entityName
                  )
                  return tanItem?.uuid || tanItem?.id || selectedTan
                })

                entityTanMasterDTOs.push({
                  userId: numericUserId,
                  entityMasterUuid: entity.uuid || entity.id,
                  tanIds: tanIds,
                })
              }
            }
          })
        })

        // Call the API
        const res = await assignUserToEntity(entityTanMasterDTOs)
        const response = res.data || {}

        if (response.success && response.data !== null) {
          // Add new assignments to local state for UI update
          const newAssignments = []

          assignEntityRows.forEach(row => {
            // Create combinations of entities and TANs
            row.entityNames.forEach(entityName => {
              row.entityTANs.forEach(taxDeductionAccountNumber => {
                // Check if this entity-TAN combination is valid using the TAN list
                const isValidCombination = tan.some(
                  tanItem =>
                    tanItem.entityName === entityName &&
                    tanItem.tan === taxDeductionAccountNumber
                )

                if (isValidCombination) {
                  newAssignments.push({
                    id: Date.now() + Math.random() * 1000, // Ensure unique IDs
                    userId: row.userId,
                    username: row.username,
                    email: row.email,
                    businessRole: row.businessRole,
                    entityName: entityName,
                    taxDeductionAccountNumber: taxDeductionAccountNumber,
                    assignedBy: 'Current User',
                    assignedDate: currentDate,
                    updatedBy: 'Current User',
                    lastModifiedDate: currentDate,
                  })
                }
              })
            })
          })

          await loadAllData(1, itemsPerPage, [])
          showSnackbar(response.data, 'success')
        } else {
          const errorMsg =
            response.errors && response.errors.length > 0
              ? response.errors[0].message
              : 'Failed to Assign Entity'
          setFormErrors({ general: errorMsg })
          setFormLoading(false)
          return
        }
      }

      setFormLoading(false)
      setModalOpen(false)
      setEditingAssignment(null)

      // Reset form state
      setAssignEntityRows([
        {
          id: 1,
          userId: '',
          username: '',
          email: '',
          businessRole: [],
          entityNames: [],
          entityTANs: [],
          ssoUser: false,
        },
      ])
    } catch (error) {
      setFormLoading(false)
      if (error instanceof z.ZodError) {
        // Process each error
        const formErrors = {}
        error.issues.forEach(err => {
          formErrors[err.path[0]] = err.message
        })
        setFormErrors(formErrors)
      } else {
        setFormErrors({
          general: error.message || 'An unexpected error occurred',
        })
      }
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingAssignment(null)
    setFormErrors({})
  }

  // Pagination handlers
  const handlePageChange = page => {
    loadAllData(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadAllData(1, newItemsPerPage, getValidCriteria(searchCriteria))
  }

  // search
  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadAllData(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadAllData(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  return (
    <Box>
      {/* Sub Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500
          }}
        >
          Assign Entity
        </Typography>

        {/* Right side - Action buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'relative',
          }}
        >
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <IconButton
              onClick={handleDownload}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton
              onClick={handleSearch}
            >
              <SearchIconBtn />
            </IconButton>
          </Tooltip>

          {roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>

              <Button
                variant="contained"
                onClick={handleAssignEntity}
                sx={{
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                Assign Entity
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Table - Using filtered assignments for search functionality */}
      <Box>
        <CommonTable
          columns={columns}
          data={assignments}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          showEdit={false}
          emptyMessage="No user found"
          showFooter={true}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          showTotalItems={true}
          showPageNumbers={true}
          showActions={roleConfig.canEdit}
        />
      </Box>

      {/* Assign Entity Modal */}
      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        title="Assign Entity"
        maxWidth="sm"
      >
        <AssignEntityForm
          assignEntityRows={assignEntityRows}
          entities={entities}
          activeUsers={activeUsers}
          tanList={tan}
          formErrors={formErrors}
          formLoading={formLoading}
          editingAssignment={false}
          onRowChange={handleAssignEntityRowChange}
          onAddRow={handleAddAssignEntityRow}
          onRemoveRow={handleRemoveAssignEntityRow}
          onSubmit={handleFormSubmit}
        />
      </CommonModal>

      {/* Edit User Modal */}
      <CommonModal
        open={editUserOpen}
        onClose={() => setEditUserOpen(false)}
        title={'Edit Assign Entity'}
        maxWidth={'sm'}
      >
        <AssignEntityForm
          assignEntityRows={assignEntityRows}
          entities={entities}
          activeUsers={activeUsers}
          tanList={tan}
          formErrors={formErrors}
          formLoading={formLoading}
          editingAssignment={false}
          onRowChange={handleAssignEntityRowChange}
          onAddRow={handleAddAssignEntityRow}
          onRemoveRow={handleRemoveAssignEntityRow}
          onSubmit={handleFormSubmit}
        />
      </CommonModal>

      {/* User Change Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Assignment"
        message={`Are you sure you want to delete assignments for user "${deleteTarget?.username || ''
          }"?`}
        confirmText="Delete User Assignment"
        type="delete"
        itemType="item"
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}

export default AssignEntity
